import type { PriceTableAnalytics } from '@/scopes/prices/domain/types'
import type { IconGlyph, UdsTokens } from '@sunwebgroup/ui-components'
import type { BreadcrumbItem, CMSPageFields, LinkedAccommodationComponent } from '../types/common'
import { mapRenderingDatasourceId } from '@/cms/fields/mappers/common'
import { mapLinkedAccommodations } from '@/cms/fields/mappers/common/linkedAccommodationsMapper'
import getFlattenModels from '@/cms/fields/mappers/getFlattenModels'
import {
  CollectionInformationComponent,
  DestinationSectionComponent,
  FlattenPDPModels,
  PracticalInformationComponent,
  SitecoreFacility,
  SitecoreImagesWithTextResponse,
  SitecoreProductDetailPageFields,
  SitecoreRoomType,
  SitecoreUserRatingDetailedCategory,
  SitecoreVideoResponse,
  SkiAncillariesComponent,
  UserReviewsScores,
} from '@/cms/fields/types/product-detail-page'
import { CollectionId } from '@/scopes/common/components/CollectionCard/types'
import { parseSitecoreFloat } from '@/scopes/products/domain/utils'
import { IconGlyph as UdsIconGlyph } from '@sunwebgroup/ui-components/uds'
import { ascend, isEmpty, prop, slice, sortWith } from 'ramda'
import { getBreadcrumbItems } from './common/breadcrumbMapper'

export interface CMSProductDetailPageFields extends CMSPageFields {
  'userReviews.ratingAverage': number
  'userReviews.ratingsCount': number
  'userReviews.ratingByFacility': CMSUserReviewsFacilityRating<UdsIconGlyph>[]
  'userReviews.legacyRatingByFacility': CMSUserReviewsFacilityRating<IconGlyph>[]
  'userReviews.scoreByCategory': CMSUserReviewsCategoryScore[]
  'userReviews.mostRatedCategoryScore': CMSUserReviewsCategoryScore | null
  'userReviews.shouldShowRatings': boolean
  'PDP.url': string
  'PDP.title': string
  'PDP.media': CMSMedia[]
  'PDP.collectionName': string | null
  'PDP.collectionTheme': CMSCollectionTheme
  'PDP.roomTypes': CMSRoomType[]
  'PDP.roomTypes.additionalInfoHtml': string | null
  'PDP.rating': number
  'PDP.description': string
  'PDP.usps': string[]
  'PDP.attention': string
  'PDP.mealplans': CMSMealplans | null
  'PDP.surroundings': string[]
  'PDP.coordinates': { lat: number, lng: number }
  'PDP.facilities': CMSFacilityType[]
  'PDP.legacyFacilities': CMSFacilityType[]
  'Breadcrumb.elements': BreadcrumbItem[] | undefined
  'PDP.productId': number | null
  'PDP.id': string
  'PDP.PracticalInformation': PracticalInformationComponent | undefined
  'PDP.SkiAncillaries': SkiAncillariesComponent
  'PDP.CollectionInformation': CollectionInformationComponent | undefined
  'PDP.DestinationInformation': DestinationSectionComponent | undefined
  'PDP.pricetable.analytics': PriceTableAnalytics | null
  'PDP.pricetable.bookingStepsUrl': string
  'PDP.pricetable.priceAlertDatasourceId': string
  'PDP.bookingGateId': string
  'PDP.promotedPriceId': string
  'PDP.countryId': string
  'PDP.countryName': string
  'PDP.countryEnglishName': string
  'PDP.regionId': string
  'PDP.regionName': string
  'PDP.regionEnglishName': string
  'PDP.cityId': string
  'PDP.cityName': string
  'PDP.cityEnglishName': string
  'PDP.hasSustainabilityCertificate': boolean
  'PDP.MetaData': {
    title: string
    description: string
    image: string
  }
  'LinkedAccommodations'?: LinkedAccommodationComponent
}

export interface CMSRoomType {
  id: string
  name: string
  capacityText: string
  characteristics: string[]
  imageUrls: string[]
}

export interface CMSCollectionTheme {
  text: keyof UdsTokens
  background: keyof UdsTokens
}

export interface CMSUserReviewsFacilityRating<T> {
  icon: T
  score: number
  typeId: number
}

export interface CMSUserReviewsCategoryScore {
  score: number
  typeId: number
}

interface CMSMealplan {
  type: {
    code: string
    description: string
  }
}

export interface CMSMealplans {
  allInclusiveDescription: string | null
  description: string | null
  mealPlans: CMSMealplan[]
}

export enum CMSMediaType {
  IMAGE = 'image',
  VIDEO = 'video',
}

export interface CMSMedia {
  src: string
  alt: string
  type: CMSMediaType
  link?: string
}

export interface CMSFacilityType {
  id: number
  title: string
  icon: IconGlyph | UdsIconGlyph
  items: string[]
  order: number
  show: boolean
}

interface Geolocation {
  city: {
    id: string
    name: string
    englishName: string
  }
  country: {
    id: string
    name: string
    englishName: string
  }
  region: {
    id: string
    name: string
    englishName: string
  }
}

export function createProductDetailPageFieldsMapper() {
  function map(sitecoreFields: SitecoreProductDetailPageFields): CMSProductDetailPageFields {
    const {
      id,
      userratingsaverage,
      userratingscount,
      userratingsdetailedcategories,
      userreviewsscores,
      productname,
      imageswithtext,
      videos,
      collection,
      roomtypes,
      description,
      rating,
      attention,
      usps,
      mealplaninfo,
      locationinfo,
      'additional roomtypes info': additionalInfoHtml,
      roomtypegeneralinfo,
      latitude,
      longitude,
      facilities,
      hassustainabilitycertificate,
      models,
      deskid,
      _url,
      renderings,
    } = sitecoreFields

    const scoreByCategory = getScoreByCategory(userreviewsscores ?? [])
    const ratingsCount = userratingscount ? Number.parseInt(userratingscount, 10) : 0
    const flattenModels = getFlattenModels<FlattenPDPModels>(models ?? {}) as FlattenPDPModels
    const geolocation = getGeolocation(flattenModels)
    // For Sunweb summer sites the name of the collection and collectionId is  PDP.collectionName field from acco, but not for EWH and winter. EWH and winter
    // we need the CollectionInformationModelBuilder to get the right collection name and collection id. For this reason, we try to get the information
    // from the model builder, if it's not available, we use the sitecore field directly.
    const collectionInfo = getCollectionInformation(flattenModels)
    const collectionId = collectionInfo?.id || collection

    const media = mapImagesAndVideosToMedia(imageswithtext ?? [], videos ?? [])

    const metaDataTitle = mapMetaDataTitle(productname, parseSitecoreFloat(rating), geolocation?.region.name, geolocation?.country.name)

    return {
      'userReviews.ratingAverage': parseSitecoreFloat(userratingsaverage),
      'userReviews.ratingsCount': ratingsCount,
      'userReviews.ratingByFacility': mapRatingByFacility<UdsIconGlyph>(userratingsdetailedcategories ?? []),
      'userReviews.legacyRatingByFacility': mapRatingByFacility<IconGlyph>(userratingsdetailedcategories ?? [], true),
      'userReviews.scoreByCategory': scoreByCategory,
      'userReviews.mostRatedCategoryScore': getMostRatedCategoryScore(scoreByCategory),
      'userReviews.shouldShowRatings': ratingsCount >= 3,
      'PDP.id': id ?? '',
      'PDP.url': _url,
      'PDP.title': productname ?? '',
      'PDP.media': media,
      'PDP.collectionName': collectionInfo?.title || collection || null,
      'PDP.collectionTheme': mapCollectionTheme(collectionId),
      'PDP.roomTypes': mapRoomTypes(roomtypes ?? []),
      'PDP.roomTypes.additionalInfoHtml': additionalInfoHtml ?? roomtypegeneralinfo ?? null,
      'PDP.rating': parseSitecoreFloat(rating),
      'PDP.description': description ?? '',
      'PDP.usps': usps ?? [],
      'PDP.attention': attention ?? '',
      'PDP.mealplans': mealplaninfo
        ? {
            allInclusiveDescription: mealplaninfo.AllInclusiveDescription,
            description: mealplaninfo.Description,
            mealPlans:
              mealplaninfo.MealPlans?.map(({ Type }) => ({
                type: {
                  code: Type.Code,
                  description: Type.Description,
                },
              })) ?? [],
          }
        : { allInclusiveDescription: null, description: null, mealPlans: [] },
      'PDP.surroundings': locationinfo ?? [],
      'PDP.coordinates': {
        lat: parseSitecoreFloat(latitude),
        lng: parseSitecoreFloat(longitude),
      },
      'PDP.facilities': facilities ? mapFacilitiesByType<UdsIconGlyph>(facilities) : [],
      'PDP.legacyFacilities': facilities ? mapFacilitiesByType<IconGlyph>(facilities, true) : [],
      'Breadcrumb.elements': getBreadcrumbItems(flattenModels.BreadcrumbModel),
      'PDP.productId': deskid ?? 0,
      'PDP.PracticalInformation': getPracticalInformation(flattenModels),
      'PDP.SkiAncillaries': getSkiAncillaries(flattenModels),
      'PDP.CollectionInformation': collectionInfo,
      'PDP.DestinationInformation': getDestinationInformation(flattenModels),
      'PDP.pricetable.analytics': mapPriceTableAnalytics({ sitecoreFields }, geolocation),
      'PDP.pricetable.bookingStepsUrl': flattenModels.BookingGateModel?.BookingStepsUrl ?? '',
      'PDP.pricetable.priceAlertDatasourceId': flattenModels.BookingGateModel?.PriceAlertDatasourceId ?? '',
      'PDP.bookingGateId': mapRenderingDatasourceId(renderings, 'BookingGate'),
      'PDP.promotedPriceId': mapRenderingDatasourceId(renderings, 'PromotedPrice'),
      'PDP.countryId': String(geolocation?.country.id ?? ''),
      'PDP.countryName': geolocation?.country.name ?? '',
      'PDP.countryEnglishName': geolocation?.country.englishName ?? '',
      'PDP.regionId': String(geolocation?.region.id ?? ''),
      'PDP.regionName': geolocation?.region.name ?? '',
      'PDP.regionEnglishName': geolocation?.region.englishName ?? '',
      'PDP.cityId': String(geolocation?.city.id ?? ''),
      'PDP.cityName': geolocation?.city.name ?? '',
      'PDP.cityEnglishName': geolocation?.city.englishName ?? '',
      'PDP.hasSustainabilityCertificate': Boolean(hassustainabilitycertificate),
      'LinkedAccommodations': mapLinkedAccommodations(flattenModels.AccommodationPage?.LinkedAccommodations),
      'PDP.MetaData': {
        title: metaDataTitle,
        description: usps?.join(', ') ?? '',
        image: media[0]?.src ?? '',
      },
    }
  }

  function mapRatingByFacility<T>(ratingCategories: SitecoreUserRatingDetailedCategory[], isLegacy = false) {
    const ratingCategoriesIconByTypeId: Record<number, IconGlyph | UdsIconGlyph> = isLegacy
      ? {
          10: 'IconPopularVillage',
          20: 'IconTag',
          30: 'IconPinMap',
          40: 'IconBed2Persons',
          50: 'IconSpray',
          60: 'IconRoomservice',
          70: 'IconKids',
          100: 'IconAmenities',
        }
      : {
          10: 'HouseWithHeart',
          20: 'Tag',
          30: 'MapPinOnMap',
          40: 'BedFor2Persons',
          50: 'Spray',
          60: 'ServingFoodWithCloche',
          70: 'Child',
          100: 'HouseBadgePlus',
        }

    return ratingCategories.map(({ Name: typeId, Rating: score }) => ({
      icon: ratingCategoriesIconByTypeId[typeId] as T,
      score,
      typeId,
    }))
  }

  function getScoreByCategory(reviewScores: UserReviewsScores[]) {
    const totalAmount = getScoresTotalAmount(reviewScores)
    return reviewScores.map((score) => {
      return {
        score: (score.Amount / totalAmount) * 100,
        typeId: score.Type,
      }
    })
  }

  function getScoresTotalAmount(reviewScores: UserReviewsScores[]) {
    return reviewScores.reduce((acc, score) => acc + score.Amount, 0) || 1
  }

  function getMostRatedCategoryScore(categoryScores: CMSUserReviewsCategoryScore[]) {
    if (isEmpty(categoryScores))
      return null

    return categoryScores?.reduce((maxItem, currentItem) =>
      currentItem.score > maxItem.score ? currentItem : maxItem,
    )
  }

  function mapMetaDataTitle(
    productname?: string,
    rating?: number,
    regionName?: string,
    countryName?: string,
  ) {
    const stars = rating && rating > 0
      ? '★'.repeat(Math.floor(rating)) + (rating % 1 > 0 ? '⯨' : '')
      : null

    const name = [productname, stars].filter(Boolean).join(' ')

    return [name, regionName, countryName]
      .filter(Boolean)
      .join(', ')
  }

  function mapImagesAndVideosToMedia(
    imagesWithText: SitecoreImagesWithTextResponse[],
    videos: SitecoreVideoResponse[],
  ) {
    const mappedImages
      = (imagesWithText
        ?.sort((a, b) => (a.Order < b.Order ? -1 : 1))
        .map(({ Image, Text }) => ({
          src: Image,
          alt: Text,
          type: CMSMediaType.IMAGE,
        })) as CMSMedia[]) ?? []

    const mappedVideos
      = (videos?.map(({ Link, Thumbnail, Title }) => ({
        src: Thumbnail,
        alt: Title,
        type: CMSMediaType.VIDEO,
        link: Link,
      })) as CMSMedia[]) ?? []

    const firstImages = slice(0, 2, mappedImages)
    const lastImages = slice(2, Infinity, mappedImages)

    return [...firstImages, ...mappedVideos, ...lastImages]
  }

  function mapCollectionTheme(collection?: string): CMSCollectionTheme {
    const defaultTheme: CMSCollectionTheme = {
      background: 'primitives_color_red_650',
      text: 'primitives_color_neutral_0',
    }

    if (!collection)
      return defaultTheme

    const sitecoreToThemeMap: Record<string, CMSCollectionTheme> = {
      'boutique': { background: 'primitives_color_collection_primary_boutique', text: 'primitives_color_neutral_0' },
      'cycling': { background: 'primitives_color_collection_primary_cycling', text: 'primitives_color_neutral_0' },
      'excellent': { background: 'primitives_color_collection_primary_excellent', text: 'primitives_color_neutral_0' },
      'famfun': { background: 'primitives_color_collection_primary_famfun', text: 'primitives_color_neutral_0' },
      'primavera': { background: 'primitives_color_collection_primary_primavera', text: 'primitives_color_neutral_0' },
      'secrets': { background: 'primitives_color_collection_primary_secrets', text: 'primitives_color_neutral_0' },
      'selections': { background: 'primitives_color_collection_primary_selections', text: 'primitives_color_neutral_0' },
      'villages': { background: 'primitives_color_collection_primary_villages', text: 'primitives_color_neutral_0' },
      'xperience': { background: 'primitives_color_collection_primary_xperience', text: 'primitives_color_neutral_0' },
      'atlantis': { background: 'primitives_color_collection_primary_atlantis', text: 'primitives_color_neutral_0' },
      'totally': { background: 'primitives_color_collection_primary_totally', text: 'primitives_color_neutral_0' },
      'child': { background: 'primitives_color_collection_primary_child', text: 'primitives_color_neutral_0' },
      'culinary': { background: 'primitives_color_collection_primary_culinary', text: 'primitives_color_neutral_0' },
      'pearls': { background: 'primitives_color_collection_primary_pearls', text: 'primitives_color_neutral_0' },
      'newest-discoveries': {
        background: 'primitives_color_collection_primary_newest_discoveries',
        text: 'primitives_color_neutral_0',
      },
      'romantic': { background: 'primitives_color_collection_primary_honeymoon', text: 'primitives_color_neutral_0' },
    }

    return sitecoreToThemeMap?.[collection.toLowerCase()] ?? defaultTheme
  }

  function mapRoomTypes(roomTypes: SitecoreRoomType[]) {
    return roomTypes.map(({ Id, Name, Characteristics, Occupation, Images }) => ({
      id: Id,
      name: Name,
      capacityText: Occupation,
      characteristics: Characteristics,
      imageUrls: Images,
    }))
  }

  function mapFacilitiesByType<T>(sitecoreFacilities: SitecoreFacility[], isLegacy = false): CMSFacilityType[] {
    /**
     * TITLES AND ICONS MAPPING
     * (build the facility type array according to this order!)
     0 - Undefined
     10 - Optional -Optionele faciliteiten met bijkomende kosten
     20 - Deposit - Waarborgsom
     30 - Mandatory - Verplichte bijkomende kosten
     40 - GeneralFacility - Algemeen
     50 - Restaurant - Restaurants & bars
     60 - Pools - Zwemfaciliteiten
     70 - Wellness - Wellness
     80 - Sports - Sportfaciliteiten
     90 - Kids - Kinderfaciliteiten
     100 - Regular -
     110 - Exceptional - Corona maatregelen
     */

    const facilityTypeMapping: Record<number, { title: string, icon: IconGlyph | UdsIconGlyph, order: number }>
      = isLegacy
        ? {
            40: { title: 'practical_info_facilities_type_general', icon: 'IconInformationCircle', order: 1 },
            90: { title: 'practical_info_facilities_type_children', icon: 'IconChild', order: 2 },
            10: { title: 'practical_info_facilities_type_optional', icon: 'IconPlus', order: 3 },
            20: { title: 'practical_info_facilities_type_deposit', icon: 'IconMoney', order: 4 },
            50: { title: 'practical_info_facilities_type_restaurants', icon: 'IconCutlery', order: 5 },
            80: { title: 'practical_info_facilities_type_sports', icon: 'IconSportsRun', order: 6 },
            30: { title: 'practical_info_facilities_type_additional', icon: 'IconInvoice', order: 7 },
            70: { title: 'practical_info_facilities_type_wellness', icon: 'IconWellness', order: 8 },
            60: { title: 'practical_info_facilities_type_swimming', icon: 'IconSwimmingPool', order: 9 },
            100: { title: 'practical_info_facilities_type_regular', icon: 'IconQuestionCircle', order: 10 },
            110: { title: 'practical_info_facilities_type_exceptional', icon: 'IconWarningTriangle', order: 11 },
            0: { title: 'practical_info_facilities_type_undefined', icon: 'IconQuestionCircle', order: 12 },
          }
        : {
            40: { title: 'practical_info_facilities_type_general', icon: 'InformationInCircle', order: 1 },
            90: { title: 'practical_info_facilities_type_children', icon: 'Child', order: 2 },
            10: { title: 'practical_info_facilities_type_optional', icon: 'Plus', order: 3 },
            20: { title: 'practical_info_facilities_type_deposit', icon: 'PaymentCardWithCoins', order: 4 },
            50: { title: 'practical_info_facilities_type_restaurants', icon: 'ForkAndKnive', order: 5 },
            80: { title: 'practical_info_facilities_type_sports', icon: 'PersonStretching', order: 6 },
            30: { title: 'practical_info_facilities_type_additional', icon: 'DocumentWithEuro', order: 7 },
            70: { title: 'practical_info_facilities_type_wellness', icon: 'Lotus', order: 8 },
            60: { title: 'practical_info_facilities_type_swimming', icon: 'SwimmingPool', order: 9 },
            100: { title: 'practical_info_facilities_type_regular', icon: 'QuestionMarkInCircle', order: 10 },
            110: { title: 'practical_info_facilities_type_exceptional', icon: 'ExclamationInTriangle', order: 11 },
            0: { title: 'practical_info_facilities_type_undefined', icon: 'QuestionMarkInCircle', order: 12 },
          }

    const facilitiesShown: Record<number, boolean> = {
      0: true,
      10: true,
      20: true,
      30: true,
      40: true,
      50: true,
      60: true,
      70: true,
      80: true,
      90: true,
      100: false,
      110: true,
    }

    const mappedFacilities = sitecoreFacilities.map(({ FacilityType, Facilities }) => {
      const facilityInfo = facilityTypeMapping[FacilityType]
      return {
        id: FacilityType,
        title: facilityInfo?.title ?? 'Missing title',
        icon: facilityInfo?.icon ?? ('IconAmenities' as T),
        items: Facilities,
        // Place unknown facility types at the end of the list
        order: facilityInfo?.order ?? 1000,
        show: FacilityType in facilitiesShown ? facilitiesShown[FacilityType] : true,
      }
    })

    return sortWith([ascend(prop('order'))], mappedFacilities)
  }

  function getGeolocation({ BookingGateModel }: FlattenPDPModels): Geolocation {
    return {
      country: {
        id: BookingGateModel?.Country?.Id ?? '',
        name: BookingGateModel?.Country?.Name ?? '',
        englishName: BookingGateModel?.Country?.EnglishName ?? '',
      },
      region: {
        id: BookingGateModel?.Region?.Id ?? '',
        name: BookingGateModel?.Region?.Name ?? '',
        englishName: BookingGateModel?.Region?.EnglishName ?? '',
      },
      city: {
        id: BookingGateModel?.City?.Id ?? '',
        name: BookingGateModel?.City?.Name ?? '',
        englishName: BookingGateModel?.City?.EnglishName ?? '',
      },
    }
  }

  function mapPriceTableAnalytics(
    { sitecoreFields }: { sitecoreFields: SitecoreProductDetailPageFields },
    geolocation: Geolocation,
  ) {
    if (!sitecoreFields.models) {
      return null
    }

    const breadcrumbs = `${geolocation.country.name} > ${geolocation.region.name} > ${geolocation.city.name}`

    return {
      accoId: `${sitecoreFields.deskid ?? ''}`,
      accoName: sitecoreFields.productname ?? '',
      breadcrumbs,
      stars: sitecoreFields.rating ?? '',
      rating: String(parseSitecoreFloat(sitecoreFields.userratingsaverage)),
      cityId: String(geolocation.city.id),
      regionId: String(geolocation.region.id),
      countryId: String(geolocation.country.id),
      cityEnglishName: String(geolocation.city.englishName),
      regionEnglishName: String(geolocation.region.englishName),
      countryEnglishName: String(geolocation.country.englishName),
      hasSustainabilityCertificate: sitecoreFields.hassustainabilitycertificate === 1,
    }
  }

  return { map }
}

function getCollectionInformation({ CollectionInformationModel }: FlattenPDPModels): CollectionInformationComponent {
  const collectionInfo = CollectionInformationModel

  return {
    ...(collectionInfo?.Id && { id: (collectionInfo?.Id as CollectionId) ?? undefined }),
    ...(collectionInfo?.Title && { title: collectionInfo?.Title ?? undefined }),
    ...(collectionInfo?.Description && { description: collectionInfo?.Description ?? undefined }),
    ...(collectionInfo?.Usps && { usps: collectionInfo?.Usps ?? undefined }),
  }
}

function getPracticalInformation({ PracticalInfoModel }: FlattenPDPModels): PracticalInformationComponent {
  const practicalInfo = PracticalInfoModel

  return {
    flightInformation: practicalInfo?.FlightInformation ?? null,
    busInformation: practicalInfo?.BusInformation ?? null,
  }
}

function getSkiAncillaries({ SkiAncillariesModel }: FlattenPDPModels): SkiAncillariesComponent {
  return {
    skiPass: SkiAncillariesModel?.SkiPass
      ? {
          introduction: SkiAncillariesModel.SkiPass.Introduction ?? null,
          extraInfo: SkiAncillariesModel.SkiPass.ExtraInfo ?? null,
        }
      : null,
    skiMaterial: SkiAncillariesModel?.SkiMaterial
      ? {
          introduction: SkiAncillariesModel.SkiMaterial.Introduction ?? null,
          extraInfo: SkiAncillariesModel.SkiMaterial.ExtraInfo ?? null,
        }
      : null,
    skiLessons: SkiAncillariesModel?.SkiLessons
      ? {
          introduction: SkiAncillariesModel.SkiLessons.Introduction ?? null,
          extraInfo: SkiAncillariesModel.SkiLessons.ExtraInfo ?? null,
        }
      : null,
  }
}

function getDestinationInformation({ DestinationSectionModel }: FlattenPDPModels): DestinationSectionComponent {
  const destinationInfo = DestinationSectionModel

  return {
    sectionTitle: destinationInfo?.MainTitle ?? '',
    imageUrl: destinationInfo?.ImageUri ?? '',
    regionName: destinationInfo?.RegionName ?? '',
    destinationHref: destinationInfo?.Url ?? '',
  }
}
